import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, User, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@shared/context/AuthContext';
import { theme } from '@shared/styles/theme';
import { styles } from './LoginForm.styles';

export interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * LoginForm component - Minecraft themed login form
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, error, fieldErrors, login, clearError } = useAuth();
  const [attempted, setAttempted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    clearError();

    if (!email || !password) return;

    try {
      await login(email, password);
      onSuccess?.();
    } catch {
      // Error is handled by the hook and displayed via error state
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.container}>
      <h2 style={styles.title}>{t('login.title')}</h2>

      {error && (
        <div style={styles.errorMessage} role="alert">
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span>{t(error, { defaultValue: error })}</span>
        </div>
      )}

      <div style={styles.formGroup}>
        <label htmlFor="email" style={styles.label}>
          {t('login.emailLabel')}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('login.emailPlaceholder')}
          disabled={isLoading}
          style={styles.input}
          className="minecraft-input"
        />
        {attempted && !email && <p style={styles.fieldError}>{t('validation.emailRequired')}</p>}
        {fieldErrors?.Email && <p style={styles.fieldError}>{fieldErrors.Email[0]}</p>}
      </div>

      <div style={styles.formGroup}>
        <label htmlFor="password" style={styles.label}>
          {t('login.passwordLabel')}
        </label>
        <div style={styles.passwordWrapper}>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('login.passwordPlaceholder')}
            disabled={isLoading}
            style={styles.passwordInput}
            className="minecraft-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={styles.eyeButton}
            aria-label={
              showPassword ? t('accessibility.hidePassword') : t('accessibility.showPassword')
            }
          >
            {showPassword ? (
              <EyeOff size={20} color={theme.colors.text.secondary} />
            ) : (
              <Eye size={20} color={theme.colors.text.secondary} />
            )}
          </button>
        </div>
        {attempted && !password && (
          <p style={styles.fieldError}>{t('validation.passwordRequired')}</p>
        )}
        {fieldErrors?.Password && <p style={styles.fieldError}>{fieldErrors.Password[0]}</p>}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        style={{
          ...styles.primaryButton,
          opacity: isLoading ? 0.6 : 1,
        }}
        className="minecraft-button"
      >
        <span>{isLoading ? t('login.loggingIn') : t('login.submitButton')}</span>
        <ArrowRight size={20} />
      </button>

      <div style={styles.divider}>
        <span style={styles.dividerLine} />
        <span style={styles.dividerText}>{t('login.newToMathQuest')}</span>
        <span style={styles.dividerLine} />
      </div>

      <Link to="/register" style={styles.secondaryButton} className="minecraft-button">
        <User size={20} />
        <span>{t('login.createAccount')}</span>
      </Link>
    </form>
  );
};

